/**
 * Drag-to-dismiss physics for the biometric bottom drawer.
 *
 * Pure functions, no DOM — the interaction model is the part worth getting
 * right and the part worth testing, so it lives apart from the React that
 * renders it.
 *
 * The behaviour being matched is the one every iOS sheet has: the surface
 * tracks your thumb 1:1 downward, resists upward, the scrim lightens as the
 * sheet falls so the content behind is progressively revealed, and the release
 * is judged on INTENT rather than distance — a fast flick dismisses from 20px,
 * a slow drag half way down springs back.
 */

/** Past this fraction of the sheet's height, a release dismisses. */
export const DISMISS_DISTANCE_RATIO = 0.35;

/** …or past this downward speed (px/ms), however far it travelled. */
export const DISMISS_VELOCITY = 0.45;

/**
 * How far a flick would carry if it decelerated naturally. Same projection as
 * UIScrollView's `.normal` deceleration, the model behind
 * "Designing Fluid Interfaces" (WWDC18): d/(1-d) with d = 0.998.
 */
export const projectMomentum = (velocity: number, decel = 0.998): number =>
  (velocity * decel) / (1 - decel);

/**
 * Downward drag passes through 1:1; upward drag is damped so the sheet feels
 * attached rather than stuck. Never returns a negative offset large enough to
 * lift the sheet clear of the screen edge.
 */
export const resistDrag = (dy: number): number => {
  if (dy >= 0) return dy;
  // Rubber band: the further up you pull, the less it gives.
  return -Math.sqrt(-dy) * 3;
};

/**
 * Scrim opacity as the sheet falls — the "graduated reveal" of the content
 * behind. Never reaches 0 while the sheet is still on screen, so the app
 * underneath does not read as interactive before the sheet is gone.
 */
export const scrimProgress = (offset: number, sheetHeight: number): number => {
  if (sheetHeight <= 0) return 1;
  const travelled = Math.min(1, Math.max(0, offset / sheetHeight));
  return 1 - travelled * 0.85;
};

export type DragRelease = { dismiss: boolean; projected: number };

/**
 * Judge a release. Distance OR velocity is enough on its own: requiring both
 * makes a confident flick feel ignored, and requiring only distance makes a
 * slow exploratory drag dismiss when the player was just looking behind it.
 */
export const judgeRelease = (
  offset: number,
  velocity: number,
  sheetHeight: number
): DragRelease => {
  const projected = offset + projectMomentum(Math.max(0, velocity));
  const farEnough = offset > sheetHeight * DISMISS_DISTANCE_RATIO;
  const fastEnough = velocity > DISMISS_VELOCITY;
  const flungPastMidpoint = projected > sheetHeight * 0.5;
  return { dismiss: farEnough || fastEnough || flungPastMidpoint, projected };
};
